/**
 * Stytch OAuth Authentication Hooks
 *
 * These hooks provide convenient wrappers for OAuth authentication
 * operations using the Stytch B2B headless client.
 */
import { getStytchClient } from './../client/headless-client';
import { STYTCH_DEFAULT_SESSION_DURATION_MINUTES } from '../../../core/session-durations';
/**
 * Hook to start OAuth discovery flow for Google
 */
export function useStartGoogleOAuthDiscovery() {
    return (options) => {
        const stytch = getStytchClient();
        if (!stytch) {
            throw new Error('Stytch client not ready');
        }
        return stytch.oauth.google.discovery.start({
            discovery_redirect_url: options?.discovery_redirect_url || `${window.location.origin}/auth/callback`,
            custom_scopes: options?.custom_scopes,
            provider_params: options?.provider_params,
        });
    };
}
/**
 * Hook to start OAuth discovery flow for Microsoft
 */
export function useStartMicrosoftOAuthDiscovery() {
    return (options) => {
        const stytch = getStytchClient();
        if (!stytch) {
            throw new Error('Stytch client not ready');
        }
        return stytch.oauth.microsoft.discovery.start({
            discovery_redirect_url: options?.discovery_redirect_url || `${window.location.origin}/auth/callback`,
            custom_scopes: options?.custom_scopes,
            provider_params: options?.provider_params,
        });
    };
}
/**
 * Hook to start Google One Tap for specific organization
 */
export function useStartGoogleOneTap() {
    return (options) => {
        const stytch = getStytchClient();
        if (!stytch) {
            throw new Error('Stytch client not ready');
        }
        return stytch.oauth.googleOneTap.start({
            organization_id: options.organization_id,
            login_redirect_url: options.login_redirect_url || `${window.location.origin}/auth/callback`,
            signup_redirect_url: options.signup_redirect_url || `${window.location.origin}/auth/callback`,
            cancel_on_tap_outside: options.cancel_on_tap_outside,
        });
    };
}
/**
 * Hook to start Google One Tap discovery flow
 */
export function useStartGoogleOneTapDiscovery() {
    return (options) => {
        const stytch = getStytchClient();
        if (!stytch) {
            throw new Error('Stytch client not ready');
        }
        return stytch.oauth.googleOneTap.discovery.start({
            discovery_redirect_url: options?.discovery_redirect_url || `${window.location.origin}/auth/callback`,
            cancel_on_tap_outside: options?.cancel_on_tap_outside,
        });
    };
}
/**
 * Hook to authenticate OAuth discovery
 */
export function useAuthenticateOAuthDiscovery() {
    const stytch = getStytchClient();
    return async (token) => {
        try {
            if (!stytch) {
                return {
                    success: false,
                    error: 'Stytch client not ready',
                    data: null,
                };
            }
            const result = await stytch.oauth.discovery.authenticate({
                discovery_oauth_token: token,
            });
            return {
                success: true,
                intermediate_session_token: result.intermediate_session_token,
                email_address: result.email_address,
                discovered_organizations: result.discovered_organizations,
                data: result,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to authenticate OAuth discovery',
                data: error,
            };
        }
    };
}
/**
 * Hook to authenticate OAuth
 */
export function useAuthenticateOAuth() {
    return async (token, options) => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
                data: null,
            };
        }
        try {
            const result = await stytch.oauth.authenticate({
                oauth_token: token,
                session_duration_minutes: typeof options?.session_duration_minutes === 'number'
                    ? options.session_duration_minutes
                    : STYTCH_DEFAULT_SESSION_DURATION_MINUTES,
            });
            return {
                success: true,
                session: result.member_session,
                member: result.member,
                organization: result.organization,
                // B2B oauth.authenticate does not throw on MFA — it returns a null member_session
                // with an intermediate session token and mfa_required details. Surface both so the
                // callback page can route into the MFA / org-selection flow instead of failing.
                intermediate_session_token: result.intermediate_session_token,
                mfa_required: result.mfa_required,
                data: result,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to authenticate OAuth',
                data: error,
            };
        }
    };
}
